import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Brain, Cloud, Code, Activity, ChevronRight } from 'lucide-react';
import { skillsData } from '../data/mock';

const iconMap = {
  Brain: Brain,
  Cloud: Cloud,
  Code: Code,
  Activity: Activity
};

// Warm color palette - no blue, violet, or green
const colorMap = {
  '#d987ff': '#f4b8c5', // Soft rose
  '#88a2ff': '#e8c4c4', // Dusty rose (was blue)
  '#78d692': '#f5d89a', // Soft gold
  '#ff965a': '#ffb79c', // Soft peach
  '#ffa07a': '#ffb899', // Soft coral
  '#e8c4c4': '#e8c4c4', // Dusty rose
  '#f4b8c5': '#f4b8c5'  // Soft rose
};

const getGentleColor = (color) => colorMap[color] || color;

const SkillCard = ({ skill, index, categoryColor }) => {
  const gentleColor = getGentleColor(categoryColor);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.04] transition-all">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex-1">
            <h4 className="text-white font-medium">{skill.name}</h4>
            <p className="text-white/40 text-sm">{skill.tools}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${skill.level}%` }}
              transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${gentleColor}, ${gentleColor}80)` }}
            />
          </div>
          <span className="text-white/50 text-sm w-10">{skill.level}%</span>
        </div>
      </div>
    </motion.div>
  );
};

const CategoryCard = ({ category, index, isInView }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = iconMap[category.icon];
  const gentleColor = getGentleColor(category.color);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="relative"
    >
      {/* Category Header */}
      <motion.div
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 cursor-pointer hover:border-white/20 hover:bg-white/[0.04] transition-all group backdrop-blur-sm"
        whileHover={{ y: -2 }}
        style={{ borderColor: isExpanded ? `${gentleColor}40` : undefined }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${gentleColor}20, ${gentleColor}10)` }}
            >
              <Icon size={28} style={{ color: gentleColor }} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">{category.title}</h3>
              <p className="text-white/50 text-sm">{category.skills.length} skills</p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="text-white/40" size={24} />
          </motion.div>
        </div>

        {/* Preview Tags */}
        <div className="flex flex-wrap gap-2 mt-4">
          {category.skills.slice(0, 3).map((skill, i) => (
            <span
              key={i}
              className="px-3 py-1 text-xs rounded-full border backdrop-blur-sm"
              style={{
                background: `${gentleColor}10`,
                borderColor: `${gentleColor}25`,
                color: gentleColor
              }}
            >
              {skill.name}
            </span>
          ))}
          {category.skills.length > 3 && (
            <span className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-white/50">
              +{category.skills.length - 3} more
            </span>
          )}
        </div>
      </motion.div>

      {/* Expanded Skills List */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-2">
              {category.skills.map((skill, i) => (
                <SkillCard
                  key={skill.name}
                  skill={skill}
                  index={i}
                  categoryColor={category.color}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SkillsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section id="skills" ref={sectionRef} className="py-32 bg-[#0d0d0f] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-0 w-96 h-96 bg-[#f5d89a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-[#e8c4c4]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header with Chat */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#e8c4c4]/20 to-[#f5d89a]/10 border border-[#e8c4c4]/20 flex items-center justify-center">
              <Code className="text-[#e8c4c4]" size={24} />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Skills & Tech Stack</h2>
              <p className="text-white/50">What I work with</p>
            </div>
          </motion.div>

          {/* Mini Chat */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex gap-4 items-start max-w-2xl"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ffb79c] to-[#f5d89a] flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-[#0d0d0f]">SP</span>
            </div>
            <div className="bg-gradient-to-r from-[#ffb79c]/10 to-[#f5d89a]/5 border border-[#ffb79c]/15 rounded-2xl rounded-tl-md px-5 py-3 backdrop-blur-sm">
              <p className="text-white/80">
                I work across the full stack, but my sweet spot is where <span className="text-[#ffb79c] font-medium">AI meets backend engineering</span>. 
                Click on any category below to explore my skills!
              </p>
            </div>
          </motion.div>
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {skillsData.categories.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
